import { apiUrl } from "../utils/api";
import { documentWriteError } from "./documentWriteError";
export type OfficeTarget = "web" | "desktop";
export type WorkspaceState =
  | "provisioning"
  | "active"
  | "sync_pending"
  | "closing"
  | "cleanup_pending"
  | "cleanup_blocked"
  | "closed"
  | "failed";
export interface WorkspaceStatus {
  workspaceId: string;
  state: WorkspaceState;
  lastSavedAt: string | null;
  localHash: string | null;
  syncPending: boolean;
  participants: number;
  cleanupReason: string | null;
  confirmationTag: string | null;
  confirmationRequired: boolean;
  confirmedParticipants: number;
  missingConfirmations: number;
  ownConfirmationPending: boolean;
  holdAssessment: "provider-enforced-unverified";
  residencyStatus: "deferred" | "verified" | "unverified";
}
export interface OfficeLaunch extends WorkspaceStatus {
  target: OfficeTarget;
  launchUrl: string;
}
export interface OfficeEvent {
  id: string;
  workspaceId: string;
  action: string;
  actorKind: string;
  createdAt: string;
  correlationId: string | null;
  result: string | null;
}
export interface OfficeOperations {
  global: true;
  currency: "EUR";
  /** Schwellen der Kosten-Mitteilungen in Cent; null, wenn nicht konfiguriert. Sie sperren nichts (PO 29.10.2026). */
  monthlyBudgetMinor: number | null;
  warnAtMinor: number | null;
  estimatedConsumptionMinor: string;
  reservedCostsMinor: string;
  forecastMonthEndMinor: string;
  actualAzureCostMinor: string | null;
  lastAzureReconciledAt: string | null;
  actualCostReferences: string[];
  forecastBasis: string;
  graphCalls: string;
  recoveryGraphCalls: string;
  egressBytes: string;
  activeBytes: string;
  deletedBytes: string | null;
  activeContainers: number;
  unknownUsageContainers: number;
  importBacklog: number;
  cleanupBacklog: number;
  lastSuccessfulSync: string | null;
  holdAssessment: string;
  residencyStatus: string;
  /** Dieselben Hinweise, die die Betreiber als Mitteilung bekommen. */
  costAlerts: Array<{ kind: string; title: string; body: string }>;
  tenants: Array<{
    tenantId: string;
    activeContainers: number;
    activeBytes: string;
    cleanupBacklog: number;
  }>;
  workspaces: Array<{
    workspaceId: string;
    tenantId: string;
    documentId: number;
    state: WorkspaceState;
    lastSavedAt: string | null;
    reason: string | null;
    createdAt: string;
    residencyCheckDeferred: boolean;
    cloudBytes: string | null;
    deletedBytes: string | null;
    usageUnknown: boolean;
    storageMeasuredAt: string | null;
    reservedMicros: string;
    egressBytes: string;
    retryAt: string | null;
    attempts: number | null;
    holdAssessment: string;
  }>;
}
async function request<T>(
  path: string,
  body?: object,
  readResponse: (response: Response) => Promise<T> = (response) =>
    response.json(),
  timeoutMs?: number,
): Promise<T> {
  let response: Response;
  const signal = timeoutMs ? AbortSignal.timeout(timeoutMs) : undefined;
  try {
    response = await fetch(
      apiUrl(path),
      body === undefined
        ? signal && { signal }
        : {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
            ...(signal ? { signal } : {}),
          },
    );
  } catch (e) {
    // Das Popup ist während des Öffnens gesperrt; das Zeitlimit ist der Ausweg.
    if (signal?.aborted || (e instanceof Error && e.name === "TimeoutError"))
      throw new Error(
        "Das Öffnen dauert ungewöhnlich lange. Die Arbeitskopie wird im Hintergrund weiter vorbereitet – bitte in einer Minute erneut öffnen.",
      );
    throw new Error(
      "Keine Verbindung zum Server. Die Arbeitskopie bleibt erhalten; bitte später erneut versuchen.",
    );
  }
  if (!response.ok)
    throw await documentWriteError(
      response,
      response.status === 403
        ? "Für diese Aktion fehlt die Berechtigung."
        : "Die Office-Aktion ist fehlgeschlagen. Die Arbeitskopie bleibt erhalten.",
    );
  return readResponse(response);
}
const path = (id: number, action: string) => `documents/${id}/office/${action}`;
export const speEditingService = {
  open: (id: number, target: OfficeTarget) =>
    request<OfficeLaunch>(path(id, "open"), { target }, undefined, 90_000),
  status: (id: number) =>
    request<WorkspaceStatus | null>(
      path(id, "workspace"),
      undefined,
      async (response) => {
        // Nest sendet bei null ohne vorhandene Arbeitskopie einen leeren HTTP-Body.
        const text = await response.text();
        return text.trim() === "" ? null : JSON.parse(text);
      },
    ),
  sync: (id: number) => request<WorkspaceStatus>(path(id, "sync"), {}),
  close: (id: number) => request<WorkspaceStatus>(path(id, "close"), {}),
  confirmSaved: (id: number, confirmationTag: string) =>
    request<WorkspaceStatus>(path(id, "confirm-saved"), { confirmationTag }),
  /** Nur Admins: bestätigt für Bearbeiter, die selbst nicht mehr bestätigen können. */
  confirmSavedOnBehalf: (id: number, confirmationTag: string) =>
    request<WorkspaceStatus>(path(id, "confirm-saved"), {
      confirmationTag,
      onBehalf: true,
    }),
  events: (id: number) => request<OfficeEvent[]>(path(id, "events")),
  operations: () => request<OfficeOperations>("spe/operations"),
};
